import { Button } from '@ui/shared/Button';
import uniqBy from 'lodash/uniqBy';
import * as React from 'react';
import styled from 'styled-components';
import {
  IPermission,
  IPermissionOption,
  ISelectOption,
  IUser,
} from '../../../../data/types';
import { CardHeader, CardHeaderLeft } from '../../../shared/card/CardHeader';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { PermissionsSelect } from '../../../shared/form/selects/PermissionsSelect';
import { TeamSelect } from '../../../shared/form/selects/TeamSelect';

interface IProps {
  existingPermissions: IPermission[];
  requestingUser: IUser;
  onChange: (perms: IPermission[]) => void;
}

export const AddPermissionForm = (props: IProps): React.ReactElement => {
  const [team, setTeam] = React.useState<ISelectOption<string>>(null);
  const [permission, setPermission] = React.useState<IPermissionOption>(null);
  const addHandler = (): void => {
    const newPerm: IPermission = {
      teamName: team.label,
      role: permission.value.write ? 'Team Admin' : 'Team User',
      orgId: props.requestingUser.orgId,
      ...permission.value,
    };
    const newPerms = uniqBy(
      (props.existingPermissions ?? []).concat([newPerm]),
      (perm: IPermission): string => perm.id
    );
    setTeam(null);
    setPermission(null);
    props.onChange(newPerms);
  };
  return (
    <Container>
      <CardHeader noPadding style={{ paddingBottom: '10px' }}>
        <CardHeaderLeft>
          <h6>Add Permission</h6>
        </CardHeaderLeft>
      </CardHeader>

      <ControlSpacer>
        <ControlLabel>Select Team</ControlLabel>
        <TeamSelect
          value={team}
          onChange={setTeam}
          allowedTeams={
            props.requestingUser.claims?.orgAdmin
              ? null
              : props.requestingUser?.adminOf
          }
        />
      </ControlSpacer>
      <ControlSpacer>
        <PermissionsSelect
          teamId={team?.value}
          value={permission}
          onChange={setPermission}
        />
      </ControlSpacer>
      <ControlSpacer>
        <Button
          onClick={addHandler}
          disabled={!team || !permission}
          icon='add'
          title='Add Permission'
          style={{ whiteSpace: 'nowrap' }}
        />
      </ControlSpacer>

      {permission?.value ? (
        permission?.value?.write === true ? (
          <TeamAdminDescription />
        ) : (
          <TeamUserDescription />
        )
      ) : null}
    </Container>
  );
};

const Container = styled.div`
  padding: 10px;
`;

const TeamAdminDescription = (): React.ReactElement => {
  return (
    <DescriptionWrapper>
      A Team Administrator can access this team's data and can add or remove
      other users from this team.
    </DescriptionWrapper>
  );
};

const TeamUserDescription = (): React.ReactElement => {
  return (
    <DescriptionWrapper>
      A Team User can access this team's data but cannot add or remove other
      users from this team.
    </DescriptionWrapper>
  );
};

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-top: 25px;
`;
